import { useState, useMemo } from 'react';
import { useMetaData, useApi } from '../hooks/useApi';
import { useCategory } from '../context/CategoryContext';
import { useMonth } from '../context/MonthContext';
import { Brand } from '../utils/api';
import { BarChart } from '../components/Charts';
import { COLORS } from '../utils/theme';

// Latest month with 1-hour-continuous session data (legacy table only).
const ONE_HR_MAX_MONTH = '2026-04';

// Categories that support the 1-hour-continuous session toggle (newer-dataset
// labels). Only these carry 1hr columns in the legacy table.
const ONE_HR_CATEGORIES = [
  'Ride Hailing & Taxi / Ride Hailing',
  'Food Delivery /',
];

function fmtM(n) {
  if (n == null) return '—';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return Number(n).toFixed(0);
}

function KpiTile({ label, value, sub }) {
  return (
    <div className="dj-kpi-tile">
      <div className="dj-kpi-value">{value}</div>
      <div className="dj-kpi-label">{label}</div>
      {sub && <div className="dj-kpi-sub">{sub}</div>}
    </div>
  );
}

function HBar({ title, apps, values, color, xFormatter }) {
  if (!apps.length) return <div className="dj-chart-card dj-chart-empty">No data</div>;
  return (
    <div className="dj-chart-card">
      <div className="dj-chart-title">{title}</div>
      <BarChart
        categories={apps}
        series={[{ name: title, data: values, color }]}
        height={Math.max(180, apps.length * 34 + 56)}
        horizontal
        xFormatter={xFormatter}
      />
    </div>
  );
}

function CategorySection({ title, rows, color, windowLabel }) {
  if (!rows || !rows.length) return null;

  // Order by App Visit Monthly Share % (1st chart) desc; all charts reuse this order
  const sorted   = [...rows].sort((a, b) => (b.visit_share_15min_pct || 0) - (a.visit_share_15min_pct || 0));
  const apps     = sorted.map(r => r.application);

  return (
    <div className="dj-section">
      <div className="dj-section-header">
        <span className="dj-section-dot" style={{ background: color }} />
        <h2 className="dj-section-title">{title}</h2>
      </div>

      <div className="dj-kpi-row">
        <KpiTile
          label="Total Sessions"
          value={fmtM(rows[0]?.cat_visits_15min)}
          sub={windowLabel}
        />
        <KpiTile
          label="Category Users"
          value={fmtM(rows[0]?.cat_users)}
        />
        <KpiTile
          label="Panel Size"
          value={fmtM(rows[0]?.panel_size)}
          sub="30-day rolling"
        />
      </div>

      <div className="dj-charts-row">
        <HBar
          title={`App Visit Monthly Share % (${windowLabel})`}
          apps={apps}
          values={sorted.map(r => +(r.visit_share_15min_pct ?? 0))}
          color={color}
          xFormatter={v => `${v.toFixed(1)}%`}
        />
        <HBar
          title="Penetration %"
          apps={apps}
          values={sorted.map(r => +(r.penetration_pct ?? 0))}
          color={color}
          xFormatter={v => v < 1 ? `${v.toFixed(2)}%` : `${v.toFixed(1)}%`}
        />
        <HBar
          title="Average User Visits"
          apps={apps}
          values={sorted.map(r => +(r.avg_visits_15min ?? 0))}
          color={color}
          xFormatter={v => v.toFixed(1)}
        />
      </div>
    </div>
  );
}

const CAT_COLORS = {
  'Ride Hailing': COLORS.teal,
  'Food Delivery': COLORS.blue,
};

export default function DoubleJeopardyView() {
  const { category: globalCategory }   = useCategory();
  const { month }                      = useMonth();
  const { sessionTypes, latestMonth, loading: metaLoading } = useMetaData();

  const [sessionType, setSessionType] = useState('open_app');
  const [sessionWindow, setSessionWindow] = useState('15min');

  const selectedMonth = month || latestMonth;

  // The 1-hour-continuous session metric is only available for select categories
  // (Ride Hailing, Food Delivery), and only from the legacy table (through
  // ONE_HR_MAX_MONTH). Later months auto-snap back to the latest month with 1hr
  // data.
  const supports1hr = ONE_HR_CATEGORIES.includes(globalCategory || '');
  // Ride Hailing & Taxi always shows 1-hour sessions (its 15-min data is
  // unreliable); the toggle is hidden for it. Food Delivery keeps the toggle.
  const isRideHailing = /ride hailing/i.test(globalCategory || '');
  const effectiveWindow = isRideHailing ? '1hr' : sessionWindow;
  const is1hr = effectiveWindow === '1hr' && supports1hr;
  const snapped = is1hr && selectedMonth > ONE_HR_MAX_MONTH;
  const effMonth = snapped ? ONE_HR_MAX_MONTH : selectedMonth;

  const { data, loading, error } = useApi(
    () => Brand.doubleJeopardy(effMonth, sessionType, is1hr ? '1hr' : '15min', is1hr ? globalCategory : undefined),
    { effMonth, sessionType, is1hr, globalCategory },
    !!effMonth,
  );

  const grouped = useMemo(() => {
    if (!data?.length) return {};
    return data.reduce((acc, row) => {
      (acc[row.category] = acc[row.category] || []).push(row);
      return acc;
    }, {});
  }, [data]);

  // "All" rows — recompute visit shares against combined category totals
  const allRows = useMemo(() => {
    if (!data?.length) return [];
    const total1h    = Object.values(grouped).reduce((s, rows) => s + (rows[0]?.cat_visits_15min   || 0), 0);
    const totalUsers = Object.values(grouped).reduce((s, rows) => s + (rows[0]?.cat_users        || 0), 0);
    return data.map(r => ({
      ...r,
      cat_visits_15min:      total1h,
      cat_users:           totalUsers,
      visit_share_15min_pct: total1h ? +((r.visits_15min / total1h) * 100).toFixed(2) : 0,
    }));
  }, [data, grouped]);

  // Sections filtered by global category (from sidebar)
  const sectionsToRender = useMemo(() => {
    if (!globalCategory || globalCategory === 'ALL') {
      return [
        { key: 'ALL', title: 'All Categories', rows: allRows, color: COLORS.teal },
        ...Object.entries(grouped).map(([cat, rows]) => ({
          key: cat, title: cat, rows, color: CAT_COLORS[cat] || COLORS.teal,
        })),
      ];
    }
    const rows = grouped[globalCategory] || [];
    return [{ key: globalCategory, title: globalCategory, rows, color: CAT_COLORS[globalCategory] || COLORS.teal }];
  }, [globalCategory, grouped, allRows]);

  if (metaLoading) {
    return <div className="loading-state"><div className="spinner" /> Loading…</div>;
  }

  return (
    <div>
      <div className="filters-bar">
        <span className="filter-label">Session</span>
        <select
          className="filter-select"
          value={sessionType}
          onChange={e => setSessionType(e.target.value)}
        >
          {sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {supports1hr && !isRideHailing && (
          <>
            <span className="filter-label" style={{ marginLeft: 20 }}>Session window</span>
            <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              {[
                { key: '15min', label: '15-min' },
                { key: '1hr',   label: '1-hour' },
              ].map(opt => {
                const active = sessionWindow === opt.key;
                return (
                  <button
                    key={opt.key}
                    onClick={() => setSessionWindow(opt.key)}
                    style={{
                      padding: '6px 16px', border: 'none', cursor: 'pointer',
                      fontSize: 13, fontWeight: active ? 700 : 500,
                      background: active ? COLORS.teal : 'transparent',
                      color: active ? '#fff' : 'var(--text-secondary)',
                    }}
                  >{opt.label}</button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {is1hr && (
        <div style={{
          background: 'rgba(29,184,154,0.08)', border: '1px solid rgba(29,184,154,0.25)',
          borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)',
        }}>
          Showing <strong>1-hour continuous sessions</strong> from the legacy dataset
          {isRideHailing && <> — <strong>always used for Ride Hailing &amp; Taxi</strong> (15-min data is unreliable for this category)</>}.
          {snapped && <> Latest available month is <strong>Apr 2026</strong> — showing that instead of the selected month.</>}
        </div>
      )}

      {loading && (
        <div className="loading-state"><div className="spinner" /> Fetching data…</div>
      )}
      {error && !loading && (
        <div className="empty-state" style={{ color: 'var(--negative)' }}>Error: {error}</div>
      )}

      {!loading && !error && data && sectionsToRender.map(s => (
        <CategorySection
          key={s.key}
          title={s.title}
          rows={s.rows}
          color={s.color}
          windowLabel={is1hr ? '1-hour sessions' : '15-min sessions'}
        />
      ))}
    </div>
  );
}
